import { Lock, LockOpen, MessageCircle, Save } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
  RfpAnswerConfidenceScores,
  RfpFullQuestion,
  RfpSelectedAnswer,
  UpdateRfpQuestionAnswerRequest,
} from "../../types/rfp";

const BASE_ANSWER_KEYS = ["direct", "technical", "executive"] as const;

type AnswerKey = RfpSelectedAnswer;

const answerLabels: Record<AnswerKey, string> = {
  direct: "Answer 1",
  technical: "Answer 2",
  executive: "Answer 3",
  manual: "Answer 4",
};

const getAnswerText = (question: RfpFullQuestion, key: AnswerKey): string => {
  const answerText = key === "manual" ? question.manual_answer : question.answers[key];
  return answerText?.trim() || "No suggested answer available.";
};

const isAnswerKey = (value: string | null): value is AnswerKey =>
  value === "direct" || value === "technical" || value === "executive" || value === "manual";

const formatConfidence = (value: number | null | undefined): string =>
  typeof value === "number" && Number.isFinite(value) ? `${Math.round(value)}%` : "--%";

const getAnswerConfidence = (
  scores: RfpAnswerConfidenceScores | null | undefined,
  answerKey: AnswerKey,
): string => (answerKey === "manual" ? "--%" : formatConfidence(scores?.[answerKey]));

const getAssigneeInitials = (name: string | null | undefined): string => {
  const words = name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (words.length === 0) return "--";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0] ?? ""}${words[1][0] ?? ""}`.toUpperCase();
};

export type RfpQuestionDiscussionContext = {
  readonly questionId: string;
  readonly indexLabel: string;
  readonly answerLabel: string;
};

export type RfpQuestionFindAnswerContext = {
  readonly questionId: string;
  readonly indexLabel: string;
  readonly questionText: string;
};

export type RfpQuestionSelectAnswerContext = {
  readonly questionId: string;
  readonly selectedAnswer: RfpSelectedAnswer;
  readonly addToKnowledgeBase: boolean;
};

export type RfpQuestionDeselectAnswerContext = {
  readonly questionId: string;
};

export type RfpQuestionLockContext = {
  readonly questionId: string;
};

export type RfpQuestionUpdateAnswerContext = {
  readonly questionId: string;
  readonly payload: UpdateRfpQuestionAnswerRequest;
};

type RfpQuestionAnswerCardProps = {
  readonly question: RfpFullQuestion;
  readonly indexLabel: string;
  readonly onDiscuss?: (context: RfpQuestionDiscussionContext) => void;
  readonly onFindAnswer?: (context: RfpQuestionFindAnswerContext) => void;
  readonly onSelectAnswer?: (context: RfpQuestionSelectAnswerContext) => void | Promise<void>;
  readonly onDeselectAnswer?: (context: RfpQuestionDeselectAnswerContext) => void | Promise<void>;
  readonly onLockQuestion?: (context: RfpQuestionLockContext) => void | Promise<void>;
  readonly onUnlockQuestion?: (context: RfpQuestionLockContext) => void | Promise<void>;
  readonly onUpdateAnswer?: (context: RfpQuestionUpdateAnswerContext) => void | Promise<void>;
  readonly isSelecting?: boolean;
  readonly isDeselecting?: boolean;
  readonly isLocking?: boolean;
  readonly isUnlocking?: boolean;
  readonly isUpdating?: boolean;
};

export const RfpQuestionAnswerCard = ({
  question,
  indexLabel,
  onDiscuss,
  onFindAnswer,
  onSelectAnswer,
  onDeselectAnswer,
  onLockQuestion,
  onUnlockQuestion,
  onUpdateAnswer,
  isSelecting = false,
  isDeselecting = false,
  isLocking = false,
  isUnlocking = false,
  isUpdating = false,
}: RfpQuestionAnswerCardProps) => {
  const [selectedAnswerKey, setSelectedAnswerKey] = useState<AnswerKey>(() => {
    const initialAnswer = isAnswerKey(question.selected_answer) ? question.selected_answer : "direct";
    return initialAnswer === "manual" && question.manual_answer === null ? "direct" : initialAnswer;
  });
  const [addToKnowledgeBase, setAddToKnowledgeBase] = useState(false);
  const [isEditingAnswer, setIsEditingAnswer] = useState(false);
  const [draftAnswerText, setDraftAnswerText] = useState("");
  const [updateError, setUpdateError] = useState<string | null>(null);
  const previousSelectedAnswerKeyRef = useRef<AnswerKey | null>(null);
  const isLocked = Boolean(question.locked_by);
  const isSelectedAnswer = question.selected_answer === selectedAnswerKey;
  const isSavingSelection = isSelecting || isDeselecting;
  const isBusy = isSavingSelection || isLocking || isUnlocking || isUpdating;
  const availableAnswerKeys = useMemo<AnswerKey[]>(() => {
    const answerKeys: AnswerKey[] = [...BASE_ANSWER_KEYS];
    if (question.manual_answer !== null) {
      answerKeys.push("manual");
    }
    return answerKeys;
  }, [question.manual_answer]);

  useEffect(() => {
    const nextAnswerKey = isAnswerKey(question.selected_answer) ? question.selected_answer : "direct";
    setSelectedAnswerKey(
      nextAnswerKey === "manual" && question.manual_answer === null ? "direct" : nextAnswerKey,
    );
  }, [question.id, question.manual_answer, question.selected_answer]);

  useEffect(() => {
    setAddToKnowledgeBase(false);
  }, [question.id]);

  const answerText = useMemo(
    () => getAnswerText(question, selectedAnswerKey),
    [question, selectedAnswerKey],
  );
  const isEditableAnswerType =
    selectedAnswerKey === "direct" ||
    selectedAnswerKey === "technical" ||
    selectedAnswerKey === "executive";
  const answerTypeForUpdate: RfpSelectedAnswer = isEditableAnswerType ? selectedAnswerKey : "manual";
  const canEditAnswer = !isLocked && !isBusy;

  useEffect(() => {
    if (!isEditingAnswer) {
      setDraftAnswerText(answerText);
      setUpdateError(null);
    }
  }, [answerText, isEditingAnswer]);

  useEffect(() => {
    if (previousSelectedAnswerKeyRef.current === null) {
      previousSelectedAnswerKeyRef.current = selectedAnswerKey;
      return;
    }

    if (previousSelectedAnswerKeyRef.current !== selectedAnswerKey && isEditingAnswer) {
      setIsEditingAnswer(false);
      setUpdateError(null);
      setDraftAnswerText(answerText);
    }
    previousSelectedAnswerKeyRef.current = selectedAnswerKey;
  }, [answerText, isEditingAnswer, selectedAnswerKey]);

  const hasAssignee = Boolean(question.assigned_to_name?.trim());
  const assigneeName = hasAssignee ? question.assigned_to_name!.trim() : "Unassigned";
  const assigneeInitials = getAssigneeInitials(question.assigned_to_name);
  const commentCount = question.comment_count ?? 0;
  const rawQuestionText = question.raw_question?.trim() ?? "";

  return (
    <article
      className={`rounded-lg border p-5 ${
        isLocked
          ? "border-amber-500 bg-[#fffbeb] shadow-[0_0_0_2px_rgba(245,158,11,0.1)]"
          : "border-gray-200 bg-white"
      }`}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">
            {indexLabel}
          </span>
          {isEditingAnswer && (
            <span className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-600">
              <Lock className="h-3 w-3" />
              Editing
            </span>
          )}
          {isLocked && (
            <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
              <Lock className="h-3 w-3" />
              Locked by {question.locked_by}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasAssignee && (
            <>
              <span className="text-[11px] font-medium text-gray-500">Assigned to:</span>
              <div className="flex items-center gap-1.5">
                <div
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white ${
                    hasAssignee ? "bg-primary-500" : "bg-gray-300"
                  }`}
                >
                  {assigneeInitials}
                </div>
                <span className="text-xs font-medium text-gray-800">{assigneeName}</span>
              </div>
            </>
          )}
          
          {!isLocked && (
            <button
              type="button"
              onClick={() => onLockQuestion?.({ questionId: question.id })}
              disabled={isBusy}
              className={`inline-flex items-center gap-1 rounded border px-2.5 py-1 text-xs font-medium ${
                isBusy
                  ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-500"
                  : "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100"
              }`}
              aria-busy={isLocking}
            >
              <Lock className="h-3.5 w-3.5" />
              {isLocking ? "Locking..." : "Lock Question"}
            </button>
          )}
          {isLocked && question.can_unlock && (
            <button
              type="button"
              onClick={() => onUnlockQuestion?.({ questionId: question.id })}
              disabled={isBusy}
              className={`inline-flex items-center gap-1 rounded border px-2.5 py-1 text-xs font-medium ${
                isBusy
                  ? "cursor-not-allowed border-gray-300 bg-gray-100 text-gray-500"
                  : "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              }`}
              aria-busy={isUnlocking}
            >
              <LockOpen className="h-3.5 w-3.5" />
              {isUnlocking ? "Unlocking..." : "Unlock Question"}
            </button>
          )}
        </div>
      </div>

      <div className="mb-4 space-y-1">
        <p className="text-[15px] font-medium text-gray-900">
          {question.better_question || question.raw_question}
        </p>
        {rawQuestionText ? (
          <p className="text-xs text-gray-500">
            <span className="font-medium text-gray-600">Raw Question:</span> {rawQuestionText}
          </p>
        ) : null}
      </div>

      <div className="mb-3 flex flex-wrap gap-2 border-b border-gray-200">
        {availableAnswerKeys.map((answerKey) => {
          const isActive = selectedAnswerKey === answerKey;
          return (
            <button
              key={answerKey}
              type="button"
              disabled={isLocked || isBusy}
              onClick={() => setSelectedAnswerKey(answerKey)}
              className={`relative px-4 py-2 text-sm ${
                isActive
                  ? "border-b-2 border-primary-500 font-semibold text-primary-600"
                  : "border-b-2 border-transparent text-gray-500"
              } ${isLocked || isBusy ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
            >
              <span>{answerLabels[answerKey]}</span>
              <span
                className={`ml-2 rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                  isActive ? "bg-primary-100 text-primary-600" : "bg-gray-100 text-gray-500"
                }`}
              >
                {getAnswerConfidence(question.confidence_scores, answerKey)}
              </span>
            </button>
          );
        })}
        <button
          type="button"
          disabled={isLocked || isBusy}
          onClick={() =>
            onFindAnswer?.({
              questionId: question.id,
              indexLabel,
              questionText: question.better_question || question.raw_question,
            })
          }
          className={`px-4 py-2 text-sm font-medium text-primary-600 ${
            isLocked || isBusy ? "cursor-not-allowed opacity-50" : ""
          }`}
        >
          Update Answer
        </button>
      </div>

      <div className="mb-3">
        <span className="inline-flex rounded border border-emerald-300 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
          AI Confidence: {getAnswerConfidence(question.confidence_scores, selectedAnswerKey)}
        </span>
      </div>

      <div className="mb-3">
        {isEditingAnswer ? (
          <div className="rounded border border-gray-200 bg-white p-2">
            <textarea
              value={draftAnswerText}
              onChange={(event) => {
                setDraftAnswerText(event.target.value);
                if (updateError) {
                  setUpdateError(null);
                }
              }}
              rows={8}
              className="w-full resize-y rounded border border-primary-400 bg-white p-3 text-sm leading-6 text-slate-700 outline-none transition-all focus:shadow-[0_0_0_2px_rgba(0,156,229,0.15)]"
            />
            {updateError ? (
              <p className="mt-2 text-sm text-red-600" role="alert">
                {updateError}
              </p>
            ) : null}
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={async () => {
                  if (!draftAnswerText.trim()) {
                    setUpdateError("Answer text cannot be empty.");
                    return;
                  }
                  setUpdateError(null);
                  try {
                    await onUpdateAnswer?.({
                      questionId: question.id,
                      payload: {
                        answer_text: draftAnswerText.trim(),
                        answer_type: answerTypeForUpdate,
                      },
                    });
                    setIsEditingAnswer(false);
                  } catch (error) {
                    const message = error instanceof Error ? error.message : "Failed to update answer";
                    setUpdateError(message);
                  }
                }}
                disabled={isBusy || !draftAnswerText.trim()}
                className="inline-flex items-center gap-1.5 rounded bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                <Save className="h-4 w-4" />
                {isUpdating ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraftAnswerText(answerText);
                  setUpdateError(null);
                  setIsEditingAnswer(false);
                }}
                disabled={isBusy}
                className="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`rounded border border-gray-200 bg-gray-50 p-3 ${
              canEditAnswer ? "cursor-text" : ""
            } ${isLocked ? "opacity-60" : ""}`}
            onClick={() => {
              if (!canEditAnswer) return;
              setDraftAnswerText(answerText);
              setUpdateError(null);
              setIsEditingAnswer(true);
            }}
            onKeyDown={(event) => {
              if (!canEditAnswer) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setDraftAnswerText(answerText);
                setUpdateError(null);
                setIsEditingAnswer(true);
              }
            }}
            role={canEditAnswer ? "button" : undefined}
            tabIndex={canEditAnswer ? 0 : -1}
            aria-label={canEditAnswer ? "Edit answer" : undefined}
          >
            <p className="whitespace-pre-wrap text-sm leading-6 text-gray-700">{answerText}</p>
          </div>
        )}
      </div>

      {!isEditingAnswer && <div className="flex flex-wrap items-center gap-4">
        <button
          type="button"
          disabled={isLocked || isBusy}
          onClick={() => {
            if (isSelectedAnswer) {
              onDeselectAnswer?.({
                questionId: question.id,
              });
              return;
            }
            onSelectAnswer?.({
              questionId: question.id,
              selectedAnswer: selectedAnswerKey,
              addToKnowledgeBase,
            });
          }}
          className={`rounded px-4 py-2 text-sm font-medium ${
            isLocked || isBusy
              ? "cursor-not-allowed bg-gray-300 text-white"
              : isSelectedAnswer
                ? "border border-red-300 bg-white text-red-600"
                : "bg-primary-500 text-white"
          }`}
          aria-busy={isBusy}
        >
          {isSelecting
            ? "Selecting..."
            : isDeselecting
              ? "Deselecting..."
              : isSelectedAnswer
                ? "Deselect Answer"
                : "Select This Answer"}
        </button>
        {!isSelectedAnswer && (
          <label className={`flex items-center gap-2 text-sm ${isLocked ? "text-gray-400" : "text-body"}`}>
            <input
              type="checkbox"
              checked={addToKnowledgeBase}
              onChange={(event) => setAddToKnowledgeBase(event.target.checked)}
              disabled={isLocked || isBusy}
              className="h-4 w-4 rounded accent-primary-500"
            />
            Add to Knowledge Base
          </label>
        )}
        <button
          type="button"
          disabled={isLocked || isBusy}
          onClick={() =>
            onDiscuss?.({
              questionId: question.id,
              indexLabel,
              answerLabel: answerLabels[selectedAnswerKey],
            })
          }
          className={`relative inline-flex h-9 w-9 items-center justify-center rounded border border-gray-300 ${
            isLocked || isBusy ? "cursor-not-allowed text-gray-400" : "text-gray-600"
          }`}
          aria-label="Discuss answer"
        >
          <MessageCircle className="h-4 w-4" />
          {commentCount > 0 ? (
            <span className="absolute -right-1 -top-1 inline-flex min-h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-semibold leading-none text-white">
              {commentCount}
            </span>
          ) : null}
        </button>
      </div>}
    </article>
  );
};
